/**
 * Centralized API client that automatically attaches access tokens to requests.
 *
 * This module provides a fetch wrapper that:
 * 1. Automatically retrieves the access token from session storage
 * 2. Attaches it to all outgoing requests
 * 3. Handles common error scenarios
 *
 * Usage:
 * Instead of manually passing accessToken to every service call,
 * services can use this client which handles auth automatically.
 */

import i18n from '../helpers/i18n';

const AUTH_USER_STORAGE_KEY = 'koala.sso.user';

type UserProfile = {
    access_token?: string;
    [key: string]: unknown;
};

/**
 * Retrieves the current access token from session storage.
 * Returns undefined if no user is logged in or token is not available.
 */
export function getAccessToken(): string | undefined {
    if (typeof window === 'undefined') {
        return undefined;
    }

    try {
        const raw = sessionStorage.getItem(AUTH_USER_STORAGE_KEY);
        if (!raw) {
            return undefined;
        }

        const user: UserProfile = JSON.parse(raw);
        return typeof user.access_token === 'string' ? user.access_token : undefined;
    } catch {
        return undefined;
    }
}

export interface ApiClientConfig {
    /**
     * Base URL for the API request.
     */
    baseUrl: string;
    /**
     * Optional override for the access token.
     * If not provided, the token will be retrieved from session storage.
     */
    accessToken?: string;
}

export interface ApiRequestOptions {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    path: string;
    body?: unknown;
    headers?: HeadersInit;
    /**
     * Content type for the request. Defaults to 'application/fhir+json'.
     */
    contentType?: string;
    /**
     * If true, skips adding the Authorization header even if a token is available.
     */
    skipAuth?: boolean;
}

export class ApiClientException extends Error {
    public readonly status: number;
    public readonly statusText: string;
    public readonly responseBody: string;
    public readonly parsedBody: unknown;

    constructor(status: number, statusText: string, responseBody: string, parsedBody: unknown) {
        super(`API request failed with status ${status}: ${responseBody || statusText || 'No error message provided'}`);
        this.name = 'ApiClientException';
        this.status = status;
        this.statusText = statusText;
        this.responseBody = responseBody;
        this.parsedBody = parsedBody;
    }
}

/**
 * Builds a full URL from base URL and path.
 */
function buildUrl(baseUrl: string, path: string): string {
    const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${normalizedBase}${normalizedPath}`;
}

/**
 * Creates an API client for making authenticated requests.
 *
 * @param config - Configuration for the API client
 * @returns Object with request methods
 */
// eslint-disable-next-line
export function createApiClient(config: ApiClientConfig) {
    const { baseUrl, accessToken: configAccessToken } = config;

    /**
     * Makes an authenticated API request.
     * The access token is automatically retrieved from session storage
     * unless explicitly provided in the config or skipAuth is true.
     */
    async function request<T>(options: ApiRequestOptions): Promise<T> {
        const { method, path, body, headers: customHeaders, contentType, skipAuth } = options;

        if (!baseUrl) {
            throw new Error('Missing API base URL');
        }

        // Get access token: prefer config override, then session storage
        const accessToken = configAccessToken ?? getAccessToken();

        const headers: HeadersInit = {
            'Content-Type': contentType ?? 'application/fhir+json',
            ...customHeaders,
        };

        // Automatically attach access token if available and not skipped
        if (!skipAuth && accessToken) {
            (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
            (headers as Record<string, string>)['X-Koala-Access-Token'] = accessToken;
        }

        const url = buildUrl(baseUrl, path);

        let response: Response;
        try {
            response = await fetch(url, {
                method,
                headers,
                body: body ? JSON.stringify(body) : undefined,
            });
        } catch (networkError) {
            console.error('[ApiClient] Network error while calling API', {
                url,
                method,
                error: networkError,
            });
            throw networkError;
        }

        if (!response.ok) {
            const errorBody = await response.text();

            // Try to parse as JSON for structured error info
            let parsedError: unknown = null;
            try {
                parsedError = JSON.parse(errorBody);
            } catch {
                // Not JSON, that's fine
            }

            // Log comprehensive error information
            console.error('[ApiClient] API responded with error', {
                url,
                method,
                status: response.status,
                statusText: response.statusText,
                body: errorBody || '(empty)',
                parsedError,
                hasAccessToken: !!accessToken,
                responseHeaders: Object.fromEntries(response.headers.entries()),
            });

            // Log a specific warning if no auth token was sent
            if (!accessToken) {
                console.warn(
                    '[ApiClient] No access token was sent with this request! Check if user is logged in and token is in session storage.',
                );
            }

            // If body is empty, provide more context
            if (!errorBody) {
                console.warn(
                    '[ApiClient] Backend returned empty error body. Check backend logs for the actual exception.',
                );
            }

            // Special handling for 401 Unauthorized (token expired)
            if (response.status === 401) {
                const errorMessage = i18n.t('Your session has expired. Please log in again.');
                throw new Error(errorMessage);
            }

            throw new ApiClientException(response.status, response.statusText, errorBody, parsedError);
        }

        // Handle empty responses
        const responseText = await response.text();
        if (!responseText || responseText.trim() === '') {
            return undefined as T;
        }

        try {
            return JSON.parse(responseText) as T;
        } catch {
            return undefined as T;
        }
    }

    return {
        request,
        get: <T>(path: string, options?: Partial<ApiRequestOptions>) => request<T>({ method: 'GET', path, ...options }),
        post: <T>(path: string, body?: unknown, options?: Partial<ApiRequestOptions>) =>
            request<T>({ method: 'POST', path, body, ...options }),
        put: <T>(path: string, body?: unknown, options?: Partial<ApiRequestOptions>) =>
            request<T>({ method: 'PUT', path, body, ...options }),
        delete: <T>(path: string, options?: Partial<ApiRequestOptions>) =>
            request<T>({ method: 'DELETE', path, ...options }),
        patch: <T>(path: string, body?: unknown, options?: Partial<ApiRequestOptions>) =>
            request<T>({ method: 'PATCH', path, body, ...options }),
    };
}

/**
 * Type for the API client instance
 */
export type ApiClient = ReturnType<typeof createApiClient>;
